import React, { FC, useState, useEffect } from 'react'

import Modal from '../../common/Modal'
import { Field, Form } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import { useTranslation } from 'react-i18next'
import { postCompanyExternalApiKeyRequest, postCompanyExternalApiKeyResponse } from '../../../../states/types/setting'
import { createCompanyExternalApiKey } from '../../../../states/containers/setting/companyExternalApiKeySlice'

interface AddExternalApiKeyModalState {
  isOpen: boolean,
  setIsOpen: (isOpen: boolean) => void,
}

interface Props {
  addExternalApiKeyModalState: AddExternalApiKeyModalState
  closeCallback: VoidFunction
  submittionCallback: (response: postCompanyExternalApiKeyResponse) => void
  closeable?: boolean
}

const AddExternalApiKeyModal: FC<Props> = (props) => {
  const {
    addExternalApiKeyModalState,
    closeCallback,
    submittionCallback,
    closeable,
  } = props

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [formState, setFormState] = useState({
    valid: false,
    pristine: true,
    submitting: false,
  })

  const handleSubmit = (values: postCompanyExternalApiKeyRequest & { apiKey: string }) => {
    dispatch(
      createCompanyExternalApiKey(
        values,
        (response) => {
          closeCallback()
          submittionCallback(response)
        },
      ),
    )
  }

  const submitForm = () => {
    const form = document.forms.namedItem('add_external_api_key')
    if (form) {
      form.dispatchEvent(
        new Event('submit', { cancelable: true, bubbles: true })
      )
    }
  }

  const {
    errors,
  } = useSelector((state: RootState) => (state))

  const renderServerValidateError = (name: string) => (
    errors.formErrors[name]
    && errors.formErrors[name].map((message: string) => (
      <p key={message} className="form-error">
        {t('validate.server_error', { name: name })}
      </p>
    ))
  )

  const initialValues = {
    label: '',
  }

  useEffect(() => {
    setFormState({
      valid: false,
      pristine: true,
      submitting: false
    })
  }, [])

  return (
    <Modal
      title="APIキーを登録"
      show={addExternalApiKeyModalState.isOpen}
      wrapperClass="setting_external_api_key"
      closeCallback={closeCallback}
      closeable={closeable}
    >
      {/* モーダルボディ */}
      <>
        <div
          style={{
            marginBottom: "32px",
          }}
        >
          識別用のラベル名を入力し、送信ボタンを押してください。
          <br />
          ※APIキーは自動生成されるため指定できません。
        </div>

        <Form<postCompanyExternalApiKeyRequest & { apiKey: string }>
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validate={values => {
            const errors: Record<string, string> = {}
            if (!values.label || !values.label.trim()) {
              errors.label = t('model.externalApiKey.error.blank', { key: t('model.externalApiKey.label') })
            }
            return errors
          }}
          render={({ handleSubmit, submitting, valid, pristine, errors }) => {
            useEffect(() => {
              setFormState({
                valid,
                pristine,
                submitting
              })
            }, [valid, pristine, submitting])

            return (
              <form name="add_external_api_key" onSubmit={handleSubmit}>
                <div className="item">
                  <label htmlFor={t('model.externalApiKey.label')}>
                    <div className="title flex">
                      <div className="label red">必須</div>
                      {t('model.externalApiKey.label')}
                    </div>
                  </label>
                  <div className="detail row_1">
                    <Field
                      type="text"
                      name="label"
                      id="label"
                      component="input"
                      className="form-control"
                      required
                    />
                    {errors?.label && (
                      <p key={errors.label} className="form-error">
                        {errors.label}
                      </p>
                    )}
                    {renderServerValidateError('label')}
                  </div>
                </div>
              </form>
            )
          }}
        />
      </>

      {/* モーダルフッター */}
      <>
        <button type="button" className="button cancel" onClick={closeCallback} disabled={!closeable}>
          キャンセル
        </button>
        <button
          type="button"
          className="button"
          onClick={submitForm}
          disabled={!formState.valid || formState.pristine || formState.submitting}
        >
          送信
        </button>
      </>
    </Modal>
  )
}

AddExternalApiKeyModal.defaultProps = {
  closeable: undefined,
}

export default AddExternalApiKeyModal
